import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/hooks/useLocale';
import { LocaleLink } from '@/components/LocaleLink';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { Navbar } from '@/components/Navbar';
import { Brand, PageHeader } from '@/components/visual/PagePrimitives';
import { PublicFooter } from '@/components/visual/PublicFooter';
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import {
  Shield, Calculator, Save, ClipboardCheck, GitCompare, BookOpen, Settings,
  LogOut, Menu, LayoutDashboard, Scale,
} from 'lucide-react';

/**
 * Enterprise application shell: persistent left navigation + compact top header
 * for signed-in users. Anonymous visitors (public calculator, marketing pages)
 * keep the simple top navigation.
 */
export function AppShell({ children, title, subtitle, actions }) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const { localePath } = useLocale();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  if (!user) {
    return (
      <div className="product-ui workspace-shell">
        <a href="#main-content" className="skip-nav-link" data-testid="shell-skip-link">{t('homepage.skip')}</a>
        <Navbar variant="light" />
        <main id="main-content" tabIndex={-1} className="site-container public-main">
          {(title || actions) && (
            <PageHeader title={title} description={subtitle} actions={actions} compact />
          )}
          {children}
        </main>
        <PublicFooter />
      </div>
    );
  }

  const primary = [
    { path: '/calculator', label: t('nav.calculator'), icon: Calculator },
    { path: '/estimates', label: t('nav.estimates'), icon: Save },
    { path: '/assessment', label: t('nav.assessment'), icon: ClipboardCheck },
    { path: '/scenarios', label: t('nav.scenarios'), icon: GitCompare },
  ];
  const secondary = [
    { path: '/methodology', label: t('trust.methodologyTitle'), icon: Scale },
    { path: '/guidance', label: t('nav.guidance'), icon: BookOpen },
    ...(user.is_admin ? [{ path: '/admin/consultants', label: t('nav.admin'), icon: LayoutDashboard }] : []),
  ];

  const isActive = (item) => location.pathname.includes(item.match || item.path.split('?')[0]);

  const linkClass = (active) =>
    `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors duration-150 ${
      active
        ? 'bg-[#1E2226] text-white font-medium border-l-2 border-[#78B928] pl-[10px]'
        : 'text-[#A9B1B8] hover:bg-[#181B1F] hover:text-white'
    }`;

  const handleLogout = async () => {
    setMobileOpen(false);
    await logout();
    navigate(localePath('/'));
  };

  const NavBody = ({ onNavigate, prefix = 'shell-nav' }) => (
    <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-6" aria-label={t('homepage.navigation')}>
      <div className="space-y-1">
        {primary.map(item => (
          <LocaleLink
            key={item.path}
            to={item.path}
            onClick={onNavigate}
            className={linkClass(isActive(item))}
            data-testid={`${prefix}-${item.path.replace(/[/?=]/g, '-').replace(/^-/, '')}`}
            aria-current={isActive(item) ? 'page' : undefined}
          >
            <item.icon className="w-4 h-4 shrink-0" aria-hidden="true" />
            {item.label}
          </LocaleLink>
        ))}
      </div>
      <div className="space-y-1 pt-4 border-t border-[#2A2F34]">
        <div className="px-3 pb-1 text-[10px] uppercase tracking-[0.14em] text-[#6B7480]"
             style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
          {t('trust.methodologyTitle')}
        </div>
        {secondary.map(item => (
          <LocaleLink
            key={item.path}
            to={item.path}
            onClick={onNavigate}
            className={linkClass(isActive(item))}
            data-testid={`${prefix}-${item.path.replace(/[/?=]/g, '-').replace(/^-/, '')}`}
            aria-current={isActive(item) ? 'page' : undefined}
          >
            <item.icon className="w-4 h-4 shrink-0" aria-hidden="true" />
            {item.label}
          </LocaleLink>
        ))}
      </div>
    </nav>
  );

  const AccountBlock = ({ onNavigate, prefix = 'shell' }) => (
    <div className="px-3 py-4 border-t border-[#2A2F34]">
      <div className="px-3 pb-2">
        <div className="text-[10px] uppercase tracking-[0.12em] text-[#6B7480]"
             style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
          {t('nav.signedInAs')}
        </div>
        <div className="mt-0.5 text-sm text-white break-words" data-testid={`${prefix}-user`}>
          {user.is_demo ? t('nav.demo') : (user.name || user.email)}
        </div>
      </div>
      <LocaleLink to="/settings" onClick={onNavigate} className={linkClass(location.pathname.includes('/settings'))} data-testid={`${prefix}-nav-settings`}>
        <Settings className="w-4 h-4 shrink-0" aria-hidden="true" /> {t('nav.settings')}
      </LocaleLink>
      <button
        onClick={handleLogout}
        className={`${linkClass(false)} w-full text-left`}
        data-testid={`${prefix}-logout-btn`}
      >
        <LogOut className="w-4 h-4 shrink-0" aria-hidden="true" /> {t('nav.logout')}
      </button>
    </div>
  );

  return (
    <div className="product-ui workspace-shell min-h-screen flex">
      <a href="#main-content" className="skip-nav-link" data-testid="shell-skip-link">{t('homepage.skip')}</a>

      {/* Desktop sidebar */}
      <aside
        className="workspace-sidebar hidden lg:flex lg:flex-col w-[240px] shrink-0 bg-[#101214] border-r border-[#2A2F34] sticky top-0 h-screen"
        data-testid="app-shell-sidebar"
      >
        <Brand testId="shell-brand" className="border-b border-[#2A2F34] text-white" />
        <NavBody />
        <AccountBlock />
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Compact top header */}
        <header
          className="workspace-header bg-white border-b border-[#DDE1E5] flex items-center gap-3 px-5 sm:px-6"
          data-testid="app-shell-header"
        >
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                className="lg:hidden inline-flex items-center justify-center w-9 h-9 rounded-md border border-[#DDE1E5] text-[#171A1D] hover:bg-[#F5F6F7]"
                aria-label={t('nav.menu')}
                data-testid="app-shell-menu-trigger"
              >
                <Menu className="w-4 h-4" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="site-drawer w-[264px] p-0 bg-[#101214] border-r border-[#2A2F34] flex flex-col" data-testid="app-shell-mobile-nav">
              <SheetTitle asChild>
                <div className="flex items-center gap-2 px-5 h-16 border-b border-[#2A2F34] text-white font-semibold">
                  <Calculator className="w-5 h-5 text-[#78B928]" aria-hidden="true" />
                  <span>ISMSCalculator</span>
                </div>
              </SheetTitle>
              <SheetDescription className="sr-only">{t('nav.menu')}</SheetDescription>
              <NavBody onNavigate={() => setMobileOpen(false)} prefix="shell-mobile-nav" />
              <AccountBlock onNavigate={() => setMobileOpen(false)} prefix="shell-mobile" />
            </SheetContent>
          </Sheet>

          <div className="workspace-header-copy">
            {title && (
              <h1 data-testid="page-title">{title}</h1>
            )}
            {subtitle && <p data-testid="page-subtitle">{subtitle}</p>}
          </div>

          <div className="page-actions">
            {actions}
            <LanguageSwitcher variant="light" />
          </div>
        </header>

        <main id="main-content" tabIndex={-1} className="workspace-content flex-1">
          {children}
        </main>
      </div>
    </div>
  );
}
